import React, { memo, useCallback } from 'react';
import { TextAlign } from '../../items/text/text-item-type';
import { changeItemsWithSelection } from '../../state/actions/change-items';
import { useWriteContext } from '../../utils/use-context';
import { useTargetItemIds } from '../utils/use-target-item-ids';
import { useSelectionValues } from '../utils/use-selection-values';
import { InspectorSubLabel } from '../components/inspector-label';
import { useTranslations } from 'next-intl';
import {
  FormatAlignCenter,
  FormatAlignLeft,
  FormatAlignRight,
} from '@crabs-ai/web-icons';
import { ToggleGroup, ToggleItem } from '@crabs-ai/ds-react';
import { TextItem } from '../../items/text/text-item-type';
import { CaptionsItem } from '../../items/captions/captions-item-type';

const TextAlignmentControlsUnmemoized: React.FC<{
  align: TextAlign;
  itemId: string;
}> = ({ align, itemId }) => {
  const t = useTranslations();
  const { setState } = useWriteContext();
  const targetIds = useTargetItemIds(itemId);
  const { isMixed } = useSelectionValues<TextItem | CaptionsItem, TextAlign>(
    itemId,
    (item) => item.align,
  );

  const setAlign = useCallback(
    (newAlign: TextAlign) => {
      setState({
        update: (state) => {
          return changeItemsWithSelection(state, itemId, targetIds, (i) => {
            if (i.type !== 'text' && i.type !== 'captions') {
              throw new Error('Text align can only be changed for text items');
            }

            return {
              ...i,
              align: newAlign,
            };
          });
        },
        commitToUndoStack: true,
      });
    },
    [setState, itemId, targetIds],
  );

  return (
    <div className="flex flex-col">
      <InspectorSubLabel>{t('Editor.inspector.text.align')}</InspectorSubLabel>
      <div className="flex items-center gap-4xs">
        <ToggleGroup
          type="single"
          value={isMixed ? '' : align}
          onValueChange={(value) => {
            if (value && typeof value === 'string')
              setAlign(value as TextAlign);
          }}
        >
          <ToggleItem
            value="left"
            aria-label="Align Left"
            isShowTooltip
            tooltipNode={t('Editor.inspector.text.align-left')}
          >
            <FormatAlignLeft />
          </ToggleItem>
          <ToggleItem
            value="center"
            aria-label="Align Center"
            isShowTooltip
            tooltipNode={t('Editor.inspector.text.align-center')}
          >
            <FormatAlignCenter />
          </ToggleItem>
          <ToggleItem
            value="right"
            aria-label="Align Right"
            isShowTooltip
            tooltipNode={t('Editor.inspector.text.align-right')}
          >
            <FormatAlignRight />
          </ToggleItem>
        </ToggleGroup>
        {isMixed && (
          <span className="text-2xs text-comp-moderate-medium">(Mixed)</span>
        )}
      </div>
    </div>
  );
};

export const TextAlignmentControls = memo(TextAlignmentControlsUnmemoized);
